import * as Actions from '../photographerPersonalCalendar/actions/CalendarActions';
import * as React from 'react';
import moment from 'moment';
import isEqual from 'react-fast-compare';
import notify from '../../shared/feedback/notifications';
import $ from 'jquery';
import * as Features from '../../shared/Features';
import { Translate } from '../../shared/LanguageService';
import { assign } from '../../shared/Assign';
import {
    ICalendarItem,
    ICalendarItemType,
    ICalendarItemBasic,
    ICalendarSettings,
    ICalendarItemUserType
} from './Types';
import CalendarModal from './CalendarModal';
import DateHelper from '../../shared/DateHelper';
import IWorkingHour from './IWorkingHour';
import { AuthenticatedUser, UserRole } from '../../shared/types';
import { getMinMaxTimeUpdate } from './calendarTimeUtils';
import 'fullcalendar';

interface IState {
    currentRangeStart: any;
    currentRangeEnd: any;
    showModal: boolean;
    canEdit: boolean;
    selectedCalendarItem: ICalendarItemBasic;
    showConfirmationModal: boolean;
    calendarItemUserType: ICalendarItemUserType;
}

interface IProps {
    settings: ICalendarSettings;
    calendarItems: ICalendarItem[];
    workingHours: IWorkingHour[];
    onChange: (events: ICalendarItem[]) => void;
    onDeleted?: (event: any) => void;
    onUpdated?: (event: any) => void;
    onCreated?: (event: any) => void;
    canUnbook?: boolean;
    onDaynameClick?: (photographerName: string) => void;
    onRangeChanged?: (from: any, to: any) => void;
    onSmsClick?: (photographerName: string) => void;
    onEmailClick?: (photographerName: string) => void;
    onPortfolioClick?: (photographerName: string, photographerId: any) => void;
    onDirectionsClick?: (photographerId: number) => void;
    onPersonalDirectionsClick?: (itemsOfTheDay: any) => void;
    selectedDate?: any;
    user: any;
}

function getLanguageKey(user: AuthenticatedUser): 'en' | 'fi' | 'sv' {
    if (user && user.details) {
        return user.details.languageKey;
    }
    
    return 'en'; // or should we assume swedish as default?
}

export class Calendar extends React.Component<IProps, IState> {
    private componentId: string = null;
    private events: ICalendarItem[] = [];
    private mounted: boolean = false;
    private defaultDate: string = null;
    private blockedEventType: number = 4;

    constructor(props: IProps, context: any) {
        super(props, context);

        this.setDayHeader = this.setDayHeader.bind(this);
        this.saveEvent = this.saveEvent.bind(this);
        this.deleteEvent = this.deleteEvent.bind(this);
        this.updateEvent = this.updateEvent.bind(this);

        this.hideModal = this.hideModal.bind(this);
        this.getSubPartUrlForOrderLink = this.getSubPartUrlForOrderLink.bind(this);
        this.unbookEvent = this.unbookEvent.bind(this);

        this.state = {
            currentRangeStart: null,
            currentRangeEnd: null,
            showModal: false,
            canEdit: false,
            selectedCalendarItem: {} as ICalendarItem,
            showConfirmationModal: false
        };

        this.componentId =
            this.props.settings.id ||
            'photographer-calendar' + Math.random().toString().replace('0.', '');
    }

    componentDidMount() {
        this.mountCalendar(this.props.settings);
    }

    unmountCalendar = () => {
        if (this.mounted) {
            $(`#${this.componentId}`).fullCalendar('destroy');
            this.mounted = false;
        }
    };

    componentWillUnmount() {
        this.unmountCalendar();
    }

    updateExistingCalendar = () => {
        let nextProps = this.props;

        this.mountCalendar(nextProps.settings);

        if (this.mounted) {
            $(`#${this.componentId}`).fullCalendar('render');
        }

        if (nextProps.calendarItems && this.mounted) {
            if (this.events !== nextProps.calendarItems) {
                this.events = nextProps.calendarItems;
                this.addCalendarEvents();
            }
        } else if (
            nextProps.settings.eventSources &&
            nextProps.settings.eventSources.length > 0 &&
            nextProps.settings.eventSources[0] &&
            nextProps.settings.eventSources[0].hasOwnProperty('events')
        ) {
            this.events = nextProps.settings.eventSources[0].events;
            this.addCalendarEvents();
        }

        if (nextProps.workingHours) {
            // Since fullcalendar is built on jQuery wee need to wait until the calendar is loaded,
            // hence setTimeout
            setTimeout(() => {
                let businessHours = nextProps.workingHours
                    .filter((h) => h.isAvailable)
                    .map((h) => {
                        return {
                            dow: [h.day],
                            start: h.isAvailable ? this.dateToTime(h.start) : '',
                            end: h.isAvailable ? this.dateToTime(h.end) : ''
                        };
                    });
                try {
                    $(`#${this.componentId}`).fullCalendar(
                        'option',
                        'businessHours',
                        businessHours
                    );
                } catch (ex) { }
            }, 100);
        }
    };

    mountNewCalendar = () => {
        this.unmountCalendar();
        this.mountCalendar(this.props.settings);
    };

    componentDidUpdate(previousProps: IProps) {
        let isNewCalendarSize = !isEqual(previousProps.settings.dayNames, this.props.settings.dayNames);

        if (isNewCalendarSize) {
            this.mountNewCalendar();
        } else {
            this.updateExistingCalendar();
        }
    }

    onRangeChanged = (from: any, to: any) => {
        /* make sure to trigger range-reload only if range actually changes */
        if (
            !moment(from).isSame(this.state.currentRangeStart) ||
            !moment(to).isSame(this.state.currentRangeEnd)
        ) {
            this.setState({ currentRangeStart: from, currentRangeEnd: to } as IState, () => {
                this.props.onRangeChanged && this.props.onRangeChanged(from, to);
            });
        }
    };

    mountCalendar(settings: any) {
        if (settings && !this.mounted) {
            /* Some views doesn't set this.events which are used when deleting/editing */
            if (
                this.events.length === 0 &&
                settings.eventSources &&
                settings.eventSources.length > 0 &&
                settings.eventSources[0] &&
                settings.eventSources[0].hasOwnProperty('events')
            ) {
                this.events = settings.eventSources[0].events;
            }

            this.defaultDate = settings.defaultDate;

            let newSettings = assign({}, settings);

            let { user } = this.props;
            let userLanguageKey = getLanguageKey(user);

            newSettings.select =
                settings.select ||
                ((start, end) => {
                    $(`#${this.componentId}`).fullCalendar('renderEvent', {
                        start: start,
                        title: '',
                        end: end,
                        id: -1
                    });

                    let newCalendarItem = {
                        title: Translate('photographer/modal/form/calendar/event-type/blocked'),
                        description: '',
                        start: new Date(
                            moment.utc(start, 'L LT', userLanguageKey).toDate()
                        ),
                        end: new Date(
                          moment.utc(end, 'L LT', userLanguageKey).toDate()
                        ),
                        id: -1,
                        editable: true,
                        type: 4,
                        orderId: null
                    } as ICalendarItemBasic;

                    this.setState({
                        selectedCalendarItem: newCalendarItem,
                        canEdit: true,
                        showModal: true
                    } as IState);
                });
            newSettings.viewRender = (view /*, element*/) => {
                this.onRangeChanged(view.start, view.end);
            };

            /* Keeps workinghours where start = end from creating a 2 hour open slot */
            newSettings.defaultTimedEventDuration = '00:00:00';
            newSettings.defaultView = settings.defaultView || 'agendaDay';
            newSettings.defaultDate = settings.defaultDate || Date.now();
            newSettings.timezone = false;
            newSettings.locale = moment.locale();
            newSettings.allDaySlot = settings.allDaySlot || false;
            newSettings.displayEventTime = settings.displayEventTime || true;
            newSettings.displayEventEnd = settings.displayEventEnd || true;
            newSettings.scrollTime = settings.scrollTime || newSettings.minTime;
            newSettings.dragScroll = settings.dragScroll || true;
            newSettings.customButtons = settings.customButtons || {
                addNewEvent: {
                    text: '+',
                    click: () => {
                        let newCalendatItem = {
                            title: Translate('photographer/modal/form/calendar/event-type/blocked'),
                            description: '',
                            start: Date.now(),
                            end: Date.now(),
                            id: -1,
                            editable: true,
                            type: 4,
                            orderId: null
                        } as any as ICalendarItemBasic;
                        this.setState({
                            selectedCalendarItem: newCalendatItem,
                            showModal: true
                        } as IState);
                    }
                }
            };
            newSettings.header = settings.header || {
                left: 'prev, next todayView',
                center: 'title',
                right: 'dayView weekView monthView addNewEvent'
            };

            newSettings.buttonText = newSettings.buttonText || {
                today: Translate('calendars/header/button/today'),
                month: Translate('calendars/header/button/month'),
                week: Translate('calendars/header/button/week'),
                day: Translate('calendars/header/button/day'),
                list: Translate('calendars/header/button/list')
            };

            newSettings.eventClick = (event, jsEvent, view) => {
                if (settings.eventClick) {
                    settings.eventClick(event, jsEvent, view);
                }

                if (event.disableClick === true) {
                    return;
                }

                let start = event.originalStart
                    ? DateHelper.transposeDate(event.start, event.originalStart)
                    : event.start;
                let end = event.originalEnd
                    ? DateHelper.transposeDate(event.end, event.originalEnd)
                    : event.end;

                if (this.props.selectedDate) {
                    start = DateHelper.transposeDate(start, this.props.selectedDate);
                    end = DateHelper.transposeDate(end, this.props.selectedDate);
                }

                let selectedCalendarItem = event as any as ICalendarItemBasic;
                this.setState({
                    canEdit: this.props.onDeleted && this.props.onCreated ? true : false,
                    selectedCalendarItem: selectedCalendarItem,
                    showModal: true
                } as IState);
            };

            newSettings.eventAfterRender = (event, jsEvent) => {
                if (jsEvent.length > 0 && event.orderNumber) {
                    let timeElements = jsEvent[0].getElementsByClassName('fc-time');
                    if (timeElements.length > 0) {
                        let orderNumber = document.createElement('span');
                        orderNumber.innerText = event.orderNumber;
                        orderNumber.className = 'event-order-number';
                        timeElements[0].appendChild(orderNumber);
                    }
                }
                jsEvent.removeClass('fc-short');
            };

            newSettings.eventDrop = (event, delta, revertFunc) => {
                let newEvent = settings.eventDrop
                    ? settings.eventDrop(event, delta, revertFunc)
                    : null;
                this.updateEvent(newEvent || event);
            };

            newSettings.eventResize = (event, delta, revertFunc) => {
                let newEvent = settings.eventResize
                    ? settings.eventResize(event, delta, revertFunc)
                    : null;
                this.updateEvent(newEvent || event);
            };

            newSettings.columnFormat = `${window.innerWidth > 600 ? 'ddd D/M' : 'ddd DD'}`;
            newSettings.weekNumbers = true;
            newSettings.weekNumberCalculation = this.props.selectedDate
                ? (date) => {
                    return moment(this.props.selectedDate).week();
                }
                : moment.locale();

            newSettings.weekNumberTitle = Translate('calendar/week-prefix');

            if (settings.workingHours) {
                newSettings.businessHours = settings.workingHours
                    .filter((h) => h.isAvailable)
                    .map((h) => {
                        return {
                            dow: [h.day],
                            start: h.isAvailable ? this.dateToTime(h.start) : '',
                            end: h.isAvailable ? this.dateToTime(h.end) : ''
                        };
                    });
            }

            $(`#${this.componentId}`).fullCalendar(newSettings);

            this.setDayHeader(this.componentId, newSettings.dayNames);
            this.addDirectionsIcon();
            this.mounted = true;
        } else if (settings) {
            /* Keeps workinghours where start = end from creating a 2 hour open slot */
            settings.defaultTimedEventDuration = '00:00:00';

            if (settings.defaultDate !== this.defaultDate) {
                this.defaultDate = settings.defaultDate;
                $(`#${this.componentId}`).fullCalendar('gotoDate', moment(this.defaultDate));
            }

            let minMaxTimeUpdate = getMinMaxTimeUpdate(settings, {
                minTime: $(`#${this.componentId}`).fullCalendar('option', 'minTime'),
                maxTime: $(`#${this.componentId}`).fullCalendar('option', 'maxTime')
            });
            if (minMaxTimeUpdate) {
                $(`#${this.componentId}`).fullCalendar('option', minMaxTimeUpdate);
            }

            if (settings.workingHours) {
                let businessHours = settings.workingHours
                    .filter((h) => h.isAvailable)
                    .map((h) => {
                        return {
                            dow: [h.day],
                            start: h.isAvailable ? this.dateToTime(h.start) : '',
                            end: h.isAvailable ? this.dateToTime(h.end) : ''
                        };
                    });
                $(`#${this.componentId}`).fullCalendar('option', 'businessHours', businessHours);
            }

            this.setDayHeader(this.componentId, settings.dayNames);
            this.addDirectionsIcon();
        }

        if (settings && settings.highlightToday === false) {
            $('.fc-today').removeClass('fc-today');
        }
        if (settings && settings.disableResizing) {
            $('.fc-resizable').removeClass('fc-resizable');
            $('.fc-resizer').removeClass('fc-resizer');
        }
    }

    addDirectionsIcon() {
        let photographWorkItems = (((this.props.user.details.role === UserRole.Photographer && this.props.onPersonalDirectionsClick)
            || (this.props.user.details.role === UserRole.Admin && this.props.onPersonalDirectionsClick)) && this.props.calendarItems.length)
            ? this.props.calendarItems.filter((event) => {
                return (event.type == ICalendarItemType['scandphotos-booking'] 
                    || event.type == ICalendarItemType['se360-booking'] 
                    || event.coordinates);
            })
            : [];
        
        if (photographWorkItems.length) {

            let headers: any = $(`#${this.componentId}`).find('.fc-day-header');

            let directionsIcon: string = `<i class="material-icons md-18 directions-car-icon personal-directions-car-icon">directions_car</i>`;

            const timestampToDate = (date: any) => new Date(date).toISOString().slice(0, 10);

            headers.toArray().forEach((header: any) => {
                let element = $(header)[0];
                const itemsOfTheDay = photographWorkItems
                    .filter((item) => timestampToDate(item.start) == header.dataset.date)
                    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

                if (itemsOfTheDay.length) {
                    let calendarData = element.children[0].innerHTML;
                    element.innerHTML = `<span>${calendarData}</span>${directionsIcon}`;
                }

                let personalDirectionsElements = $(element).find(`.personal-directions-car-icon`);

                if (this.props.onPersonalDirectionsClick && personalDirectionsElements.length) {
                    personalDirectionsElements[0].removeAttribute('click');
                    personalDirectionsElements[0].addEventListener('click', () => {
                        this.props.onPersonalDirectionsClick(itemsOfTheDay);
                    });
                }
            });
        }
    }

    setDayHeader(componentId: string, dayNames: any[]) {
        if (dayNames && dayNames.length > 0) {
            let headers: any = $(`#${componentId}`).find('.fc-day-header');
            let photographerIndex = 0;
            headers.toArray().forEach((header) => {
                let element = $(header)[0];
                let name = dayNames[photographerIndex];
                element.innerHTML = name;

                let photographerElements = $(element).find('.photographer-name');
                let photographerHeaders = $(element).find('.photographer-header');

                if (photographerElements && photographerElements.length > 0) {
                    let photographerName = photographerElements[0].innerText;
                    let photographerId = Number(photographerHeaders[0].dataset.photographerId);

                    let photographerEvents = this.props.settings.eventSources[0].events.filter((event) => {
                        return event.userId == photographerId
                            && (event.type == ICalendarItemType['scandphotos-booking']
                                || event.type == ICalendarItemType['se360-booking']);
                    });

                    if (this.props.onDaynameClick) {
                        photographerElements[0].removeAttribute('click');
                        photographerElements[0].addEventListener('click', () =>
                            this.props.onDaynameClick(photographerName)
                        );
                        photographerElements[0].classList.add('clickable');
                    }

                    let emailElements = $(element).find('.email-icon');
                    if (this.props.onEmailClick && emailElements.length > 0) {
                        emailElements[0].removeAttribute('click');
                        emailElements[0].addEventListener('click', () =>
                            this.props.onEmailClick(photographerName)
                        );
                        emailElements[0].classList.add('clickable');
                    }

                    let smsElements = $(element).find('.sms-icon');
                    if (this.props.onSmsClick && smsElements.length > 0) {
                        smsElements[0].removeAttribute('click');
                        smsElements[0].addEventListener('click', () =>
                            this.props.onSmsClick(photographerName)
                        );
                        smsElements[0].classList.add('clickable');
                    }

                    let directionsElements = $(element).find('.directions-car-icon');
                    if (photographerEvents.length && directionsElements.length) {
                        directionsElements[0].classList.remove('hide');
                    }
                    if (this.props.onDirectionsClick && directionsElements.length > 0) {
                        directionsElements[0].removeAttribute('click');
                        directionsElements[0].addEventListener('click', () =>
                            this.props.onDirectionsClick(photographerId)
                        );
                    }

                    let portfolioElements = $(element).find('.portfolio-button');
                    if (this.props.onPortfolioClick && portfolioElements.length > 0) {
                        portfolioElements[0].removeAttribute('click');
                        portfolioElements[0].addEventListener('click', () =>
                            this.props.onPortfolioClick(photographerName, photographerId)
                        );
                    }
                }

                photographerIndex++;
            });
            $(`#${this.componentId}`).find('th').css('width', 35);
            $(`#${this.componentId}`)
                .find('.fc-view')
                .css('max-width', 35 + dayNames.length * 300);
            $(`#${this.componentId}`).find('th + th').css('width', 'auto');
        }
    }

    addCalendarEvents() {
        if (this.mounted) {
            // Since fullcalendar is built on jQuery wee need to wait until the calendar is loaded,
            // hence setTimeout
            setTimeout(() => {
                $(`#${this.componentId}`).fullCalendar('removeEvents');
                $(`#${this.componentId}`).fullCalendar('addEventSource', this.events);

                if (this.props.settings.disableResizing) {
                    $('.fc-resizable').removeClass('fc-resizable');
                    $('.fc-resizer').removeClass('fc-resizer');
                }
            }, 1000);
        }
    }

    updateEvent(event) {
        for (let i = 0; i < this.events.length; i++) {
            if (this.events[i].id === event.id && this.events[i].type === event.type) {
                this.events[i].start = event.start.format();
                this.events[i].end = event.end.format();
                this.events[i].userId = event.userId;
                this.events[i].userName = event.userName;
                this.events[i].editable = event.editable;
                this.events[i].className = event.className;
                this.events[i].editable = event.editable;
                if (this.props.onUpdated) {
                    this.props.onUpdated(this.events[i]);
                }
                break;
            }
        }
        $(`#${this.componentId}`).fullCalendar('updateEvent', event);

        this.props.onChange(this.events);
    }

    saveEvent(calendarItem?: any) {
        if (calendarItem.id === -1) {
            calendarItem.id = 'event:' + new Date().getTime();

            let evt: any = {
                // add 'dummy' ID to event in order to
                // mark it as new (with string) and mark it
                // as unique to FullCalendar
                id: 'event:' + new Date().getTime(),
                title: calendarItem.title ? calendarItem.title : '',
                description: calendarItem.description ? calendarItem.description : '',
                start: moment.utc(calendarItem.start).format('YYYY-MM-DDTHH:mm:ss'),
                end: moment.utc(calendarItem.end).format('YYYY-MM-DDTHH:mm:ss'),
                type: this.blockedEventType,
                editable: true
            };

            $(`#${this.componentId}`).fullCalendar('renderEvent', evt);
            this.events.push(evt);
            this.props.onCreated(evt);
        } else {
            for (let i = 0; i < this.events.length; i++) {
                if (this.events[i].id === calendarItem.id) {
                    this.events[i].title = calendarItem.title;
                    this.events[i].description = calendarItem.description;
                    this.events[i].start = calendarItem.start;
                    this.events[i].end = calendarItem.end;
                    this.events[i].type = calendarItem.type;
                    if (this.props.onUpdated) {
                        this.props.onUpdated(this.events[i]);
                    }
                    break;
                }
            }
        }

        $(`#${this.componentId}`).fullCalendar('removeEvents');
        $(`#${this.componentId}`).fullCalendar('addEventSource', this.events);

        this.props.onChange(this.events);

        this.hideModal();
    }

    deleteEvent(calendarItem?: any) {
        let deletedEvent = this.events.filter((e) => {
            return e.id === calendarItem.id;
        })[0];
        this.props.onDeleted(deletedEvent);
        this.events = this.events.filter((e) => {
            return e.id !== calendarItem.id;
        });
        $(`#${this.componentId}`).fullCalendar('removeEvents');
        $(`#${this.componentId}`).fullCalendar('addEventSource', this.events);

        this.props.onChange(this.events);

        this.hideModal();
    }

    dateToTime(str: string) {
        let d = moment(str);
        if (!isNaN(d.toDate().getTime())) {
            return moment(d.toDate()).format('HH:mm');
        }
        return str;
    }

    getSubPartUrlForOrderLink() {
        if (Features.hasFeature(Features.ViewBasicOrderInfo, this.props.user)) {
            return '/basic';
        }

        if (Features.hasFeature(Features.ViewEditedImages, this.props.user)) {
            return '/image';
        }

        return '';
    }

    hideModal() {
        this.setState({
            showModal: false,
            showConfirmationModal: false
        } as IState);

        // Only clear these if modal is for new event
        if (this.state.selectedCalendarItem.id <= 0) {
            $(`#${this.componentId}`).fullCalendar('removeEvents');
            $(`#${this.componentId}`).fullCalendar('addEventSource', this.events);
        }
    }

    unbookEvent(eventId: number) {
        Actions.unbookEvent(eventId)
            .then(() => {
                notify.success(Translate('photographer-events/toastr/unbooking/success'));
                this.deleteEvent();
                this.hideModal();
            })
            .catch(() => {
                notify.failure(Translate('photographer-events/toastr/unbooking/error'));
            });
    }

    render() {
        const { user } = this.props;

        return (
            <div>
                {this.state.showModal && (
                    <CalendarModal
                        calendarItemUserType={
                            this.props.calendarItemUserType || ICalendarItemUserType.Photographer
                        }
                        calendarItemBasic={this.state.selectedCalendarItem}
                        closeModal={() => this.setState({ showModal: false } as IState)}
                        hideModal={() => this.hideModal()}
                        getSubPartUrlForOrderLink={() => this.getSubPartUrlForOrderLink()}
                        saveEvent={(calendarItem: any) => this.saveEvent(calendarItem)}
                        deleteEvent={(calendarItem: any) => this.deleteEvent(calendarItem)}
                        unbookEvent={(eventId: number) => this.unbookEvent(eventId)}
                        canUnbook={this.props.canUnbook}
                        canEdit={this.state.canEdit}
                        user={user}
                    />
                )}
                {this.componentId && <div id={this.componentId}></div>}
            </div>
        );
    }
}



export default Calendar;
